import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Loader2, Lock, Sparkles, TrendingUp } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { useUpgradeModal } from "../components/AppShell";

const MOODS = ["✨", "💖", "🌿", "😌", "🌧️"];

// Mood → numerical score (higher = better).
const MOOD_SCORE = { "✨": 10, "💖": 9, "🌿": 8, "😌": 7, "🌧️": 5 };

/* ProgressPhoto — resolves a stored value (path or legacy public URL) into
   a short-lived signed URL. The progress-photos bucket is private, so we
   always go through supabase.storage.createSignedUrl. */
function ProgressPhoto({ value }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    // Accept both new format (just the storage path "userId/filename")
    // and legacy public URLs (".../object/public/progress-photos/userId/filename").
    let path = value;
    const m = value.match(/progress-photos\/(.+)$/);
    if (m) path = m[1];
    if (path.startsWith("http")) {
      // Couldn't extract a path — render as-is (legacy, will 404 on private bucket).
      if (alive) setUrl(value);
      return;
    }
    supabase.storage
      .from("progress-photos")
      .createSignedUrl(path, 60 * 60) // 1 hour
      .then(({ data }) => {
        if (alive && data?.signedUrl) setUrl(data.signedUrl);
      });
    return () => {
      alive = false;
    };
  }, [value]);
  if (!url) {
    return (
      <div className="mt-3 rounded-[16px] w-full h-40 bg-cream/70 flex items-center justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-cocoa/40" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt="progress"
      className="mt-3 rounded-[16px] w-full max-h-72 object-cover"
    />
  );
}

export default function Progress() {
  const { profile } = useAuth();
  const upgrade = useUpgradeModal();
  const [view, setView] = useState("log"); // log | journey | score
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from("progress_logs")
      .select("*")
      .eq("user_id", profile.id)
      .order("created_at", { ascending: false })
      .limit(60)
      .then(({ data }) => setLogs(data || []));
  }, [profile?.id]);

  return (
    <div className="px-5 pt-2 pb-10" data-testid="progress-page">
      <div className="mb-5">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-medium mb-1">
          Your journey
        </p>
        <h1 className="font-serif text-3xl text-cocoa">Progress</h1>
      </div>

      {/* View switcher */}
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        <Tab label="Log today" active={view === "log"} onClick={() => setView("log")} testId="prog-tab-log" />
        <Tab label="Journey" active={view === "journey"} onClick={() => setView("journey")} testId="prog-tab-journey" />
        <Tab label="Glow Score" active={view === "score"} onClick={() => setView("score")} testId="prog-tab-score" />
      </div>

      {view === "log" && (
        <LogForm profile={profile} onCreated={(row) => setLogs((l) => [row, ...l])} />
      )}

      {view === "journey" && <Journey logs={logs} />}

      {view === "score" && (
        <GlowScore logs={logs} isPremium={!!profile?.is_premium} onUnlock={() => upgrade.open("Glow Score")} />
      )}
    </div>
  );
}

function Tab({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
        active ? "bg-rose text-white shadow-rose" : "bg-white text-cocoa/60 shadow-card"
      }`}
    >
      {label}
    </button>
  );
}

function LogForm({ profile, onCreated }) {
  const [note, setNote] = useState("");
  const [type, setType] = useState("skin");
  const [mood, setMood] = useState("✨");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!profile?.id) return;
    setBusy(true);
    let photo_url = null;
    if (photo) {
      const path = `${profile.id}/${Date.now()}-${photo.name}`;
      const { error: upErr } = await supabase.storage
        .from("progress-photos")
        .upload(path, photo);
      if (!upErr) {
        // Store the storage PATH (not a public URL) — bucket is private; we
        // generate short-lived signed URLs at read time.
        photo_url = path;
      }
    }
    const { data } = await supabase
      .from("progress_logs")
      .insert({
        user_id: profile.id,
        note,
        type,
        mood,
        photo_url,
        log_date: new Date().toISOString().slice(0, 10),
      })
      .select()
      .maybeSingle();
    if (data) onCreated(data);
    setNote("");
    setPhoto(null);
    setBusy(false);
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-[20px] p-5 shadow-card"
      data-testid="progress-form"
    >
      <div className="flex gap-2 mb-3">
        {["skin", "hair"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setType(t)}
            data-testid={`progress-type-${t}`}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
              type === t ? "bg-rose text-white shadow-rose" : "bg-cream text-cocoa/60"
            }`}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="How is your skin/hair feeling today?"
        required
        rows={3}
        data-testid="progress-note"
        className="w-full rounded-[16px] bg-cream border border-cocoa/5 px-4 py-3 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40 resize-none"
      />
      <div className="flex items-center gap-2 mt-3">
        <p className="text-xs text-cocoa/55 mr-1">Mood:</p>
        {MOODS.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMood(m)}
            data-testid={`mood-${m}`}
            className={`text-xl w-9 h-9 rounded-full flex items-center justify-center transition-all ${
              mood === m ? "bg-rose/15 ring-2 ring-rose" : "bg-cream"
            }`}
          >
            {m}
          </button>
        ))}
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-cocoa/60 cursor-pointer">
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setPhoto(e.target.files?.[0] || null)}
          data-testid="progress-photo"
          className="hidden"
        />
        <span className="rounded-full bg-cream px-4 py-2 inline-flex items-center gap-2 hover:bg-rose/10">
          <Camera size={14} />
          {photo ? photo.name : "Add a photo"}
        </span>
      </label>
      <button
        type="submit"
        disabled={busy}
        data-testid="progress-submit"
        className="mt-4 w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3 font-medium transition-all shadow-rose disabled:opacity-40 flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save today's log"}
      </button>
    </form>
  );
}

function Journey({ logs }) {
  return (
    <div data-testid="progress-history">
      {logs.length === 0 ? (
        <div className="bg-white rounded-[20px] p-8 shadow-card text-center">
          <Sparkles size={22} className="text-rose mx-auto mb-2" />
          <p className="text-sm text-cocoa/65 font-light">
            Start logging to see your story unfold.
          </p>
        </div>
      ) : (
        <div className="relative pl-6">
          {/* Vertical timeline rail */}
          <span className="absolute left-2 top-2 bottom-2 w-px bg-rose/20" />
          <div className="space-y-4">
            {logs.map((l) => (
              <motion.div
                key={l.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-[20px] p-4 shadow-card relative"
                data-testid={`log-${l.id}`}
              >
                <span className="absolute -left-[19px] top-5 w-3 h-3 rounded-full bg-rose ring-4 ring-cream" />
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{l.mood}</span>
                    <span className="text-[10px] uppercase tracking-wider text-cocoa/55">
                      {l.type}
                    </span>
                  </div>
                  <span className="text-xs text-cocoa/55">
                    {new Date(l.created_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <p className="text-sm text-cocoa font-light">{l.note}</p>
                {l.photo_url && <ProgressPhoto value={l.photo_url} />}
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GlowScore({ logs, isPremium, onUnlock }) {
  // Compute last-7-day Glow Score (weighted avg of mood scores).
  const scoreData = useMemo(() => {
    const now = new Date();
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const ds = d.toISOString().slice(0, 10);
      const dayLogs = logs.filter((l) => l.log_date === ds);
      const avg = dayLogs.length
        ? dayLogs.reduce((s, l) => s + (MOOD_SCORE[l.mood] || 7), 0) /
          dayLogs.length
        : null;
      days.push({ date: ds, score: avg });
    }
    return days;
  }, [logs]);

  const valid = scoreData.filter((d) => d.score != null);
  const currentScore = valid.length
    ? Math.round((valid.reduce((s, d) => s + d.score, 0) / valid.length) * 10) / 10
    : 0;
  const trend =
    valid.length >= 2
      ? valid[valid.length - 1].score - valid[0].score
      : 0;

  return (
    <div className="bg-white rounded-[20px] p-5 shadow-card relative overflow-hidden" data-testid="glow-score-section">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-medium mb-1">
            Premium
          </p>
          <h2 className="font-serif text-2xl text-cocoa">Your Glow Score</h2>
          <p className="text-xs text-cocoa/55 font-light">
            Weekly trend of your skin & hair journey.
          </p>
        </div>
        {isPremium && (
          <div className="text-right">
            <p className="font-serif text-4xl text-rose leading-none">
              {currentScore || "—"}
              <span className="text-base text-cocoa/40">/10</span>
            </p>
            {trend !== 0 && (
              <p className="text-[10px] uppercase tracking-wider text-cocoa/55 mt-1 flex items-center justify-end gap-1">
                <TrendingUp
                  size={10}
                  className={trend > 0 ? "text-rose" : "text-cocoa/40 rotate-180"}
                />
                {trend > 0 ? "+" : ""}
                {trend.toFixed(1)} this week
              </p>
            )}
          </div>
        )}
      </div>

      <ScoreChart data={scoreData} locked={!isPremium} />

      {isPremium ? (
        <p className="text-xs text-cocoa/55 font-light mt-3 leading-relaxed">
          Score is calculated from your daily mood + check-ins. Log consistently to
          see your real glow trend.
        </p>
      ) : (
        <div className="absolute inset-0 bg-white/55 backdrop-blur-md flex flex-col items-center justify-center text-center p-6 rounded-[20px]">
          <Lock className="text-gold mb-2" />
          <p className="text-xs uppercase tracking-[0.18em] text-gold font-medium mb-1">
            Premium
          </p>
          <h3 className="font-serif text-xl text-cocoa mb-2">
            Unlock your Glow Score
          </h3>
          <button
            onClick={onUnlock}
            data-testid="glow-score-unlock"
            className="rounded-full px-5 py-2 text-xs text-white"
            style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
          >
            Unlock Premium
          </button>
        </div>
      )}
    </div>
  );
}

function ScoreChart({ data, locked }) {
  const w = 320;
  const h = 110;
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * w,
    y: h - ((d.score ?? 6) / 10) * h,
    real: d.score != null,
  }));
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className={`w-full h-28 ${locked ? "blur-sm" : ""}`}
    >
      <defs>
        <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#C9748A" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#C9748A" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L ${w} ${h} L 0 ${h} Z`} fill="url(#g)" />
      <path
        d={path}
        fill="none"
        stroke="#C9748A"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={p.x}
          cy={p.y}
          r="3.5"
          fill={p.real ? "#C9748A" : "#E8B4C2"}
        />
      ))}
    </svg>
  );
}
